import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AdjustmentsHorizontalIcon,
  ArrowsRightLeftIcon,
  ArrowPathIcon,
  FolderOpenIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

import Layout from '../../components/layout/Layout';
import {
  Alert,
  Badge,
  Button,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  MultiSelect,
  MultiSelectOption,
  Page,
  PageBody,
  PageHeader,
  Panel,
  PanelBody,
  PanelHeader,
  PanelTitle,
  RadioCard,
  RadioGroup,
  Select,
  SelectOption,
  Spinner,
} from '../../components/ui';
import { useAuth } from '../../contexts/AuthContext';
import { useToasts } from '../../stores/useToasts';
import {
  generateStudioOutput,
  getApiErrorMessage,
  getStudioCompetitorContext,
  getStudioOutput,
  listBrandVoices,
  listIndexedBrandProducts,
  listCompetitorSets,
  listIndexedBrands,
  regenerateStudioOutput,
  saveStudioOutputEdits,
  updateStudioStatus,
} from './api';
import ApprovalControls from './components/ApprovalControls';
import BVPageTitle from './components/BVPageTitle';
import BrandSelector from './components/BrandSelector';
import CitationPanel from './components/CitationPanel';
import CompetitorPanel from './components/CompetitorPanel';
import ContentEditor from './components/ContentEditor';
import EvidenceChips from './components/EvidenceChips';
import EvidenceSummaryPanel from './components/EvidenceSummaryPanel';
import ProductSelector from './components/ProductSelector';
import TemplateSelector from './components/TemplateSelector';
import {
  BVBrandVoice,
  BVCompetitorSet,
  BVIndexedBrand,
  BVIndexedProduct,
  BVStudioCompetitorContext,
  BVStudioDraft,
  BVStudioOutput,
  BVTemplateType,
} from './types';
import {
  BV_TEMPLATE_OPTIONS,
  BV_RETAILER_SCOPE_OPTIONS,
  BV_VARIANT_OPTIONS,
  applyEvidenceChipToDraft,
  createEmptyStudioDraft,
  draftFromStudioOutput,
  normalizeStudioDraft,
  normalizeStudioDraftForRequest,
} from './utils';

type EntryMode = 'template-first' | 'product-first';
type PendingUnsavedAction = (() => Promise<void> | void) | null;

interface EditorState {
  title: string;
  variants: string[];
}

const TEMPLATE_PREVIEW_CONTENT: Record<BVTemplateType, string[]> = {
  social_ad: ['Headline', 'Primary message', 'Support line'],
  search_ad: ['Headline options', 'Description lines', 'Call to action'],
  pdp_snippet: ['Feature statement', 'Benefit proof', 'Product detail copy'],
  email_snippet: ['Subject line', 'Preview text', 'Email body'],
  retail_buyer_kit: ['Retail pitch', 'Proof points', 'Buyer-ready summary'],
  comparative: ['Brand positioning', 'Review-backed contrast', 'Qualified call to action'],
};

function arraysEqual(left: string[], right: string[]): boolean {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

function buildEditorState(output: BVStudioOutput | null): EditorState {
  if (!output) {
    return { title: '', variants: [''] };
  }

  return {
    title: output.title,
    variants: [output.content, ...(output.content_variants || [])],
  };
}

export default function BVReviewsStudioPage() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { push: addToast } = useToasts();
  const { hasAnyPermission } = useAuth();

  const canRead = hasAnyPermission(['bv:read', 'bv:write', 'platform:admin']);
  const canWrite = hasAnyPermission(['bv:write', 'platform:admin']);

  const [draft, setDraft] = useState<BVStudioDraft>(createEmptyStudioDraft());
  const [entryMode, setEntryMode] = useState<EntryMode>('template-first');
  const [brands, setBrands] = useState<BVIndexedBrand[]>([]);
  const [products, setProducts] = useState<BVIndexedProduct[]>([]);
  const [competitorSets, setCompetitorSets] = useState<BVCompetitorSet[]>([]);
  const [brandVoices, setBrandVoices] = useState<BVBrandVoice[]>([]);
  const [currentOutput, setCurrentOutput] = useState<BVStudioOutput | null>(null);
  const [isLoadingDependencies, setIsLoadingDependencies] = useState(true);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [productLoadError, setProductLoadError] = useState<string | null>(null);
  const [isLoadingOutput, setIsLoadingOutput] = useState(Boolean(id));
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSavingEdits, setIsSavingEdits] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isSetupOpen, setIsSetupOpen] = useState(!id);
  const [isCompareOpen, setIsCompareOpen] = useState(false);
  const [isLoadingCompetitorContext, setIsLoadingCompetitorContext] = useState(false);
  const [competitorContext, setCompetitorContext] = useState<BVStudioCompetitorContext | null>(null);
  const [competitorContextError, setCompetitorContextError] = useState<string | null>(null);
  const [competitorContextCache, setCompetitorContextCache] = useState<Record<string, BVStudioCompetitorContext>>({});
  const [editorState, setEditorState] = useState<EditorState>(() => buildEditorState(null));
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = useState(false);
  const [pendingUnsavedActionLabel, setPendingUnsavedActionLabel] = useState('continue');
  const pendingUnsavedActionRef = useRef<PendingUnsavedAction>(null);
  const productRequestSequenceRef = useRef(0);
  const productCacheRef = useRef<Record<string, BVIndexedProduct[]>>({});

  const competitorBrandOptions = useMemo(
    () => brands.filter(brand => brand.brand !== draft.brand),
    [brands, draft.brand],
  );
  const activeTemplate = useMemo(
    () => BV_TEMPLATE_OPTIONS.find(option => option.value === draft.template_type) ?? BV_TEMPLATE_OPTIONS[0],
    [draft.template_type],
  );
  const editorVariants = useMemo(
    () => (editorState.variants.length > 0 ? editorState.variants : ['']),
    [editorState.variants],
  );
  const hasWorkspace = Boolean(currentOutput);
  const canEditOutput = Boolean(currentOutput && canWrite && currentOutput.status === 'draft');
  const hasCompetitorSelection = Boolean(draft.competitor_set_id || draft.competitor_brands.length > 0);
  const hasUnsavedEdits = useMemo(() => {
    if (!currentOutput) {
      return false;
    }

    const savedVariants = [currentOutput.content, ...(currentOutput.content_variants || [])];
    return editorState.title.trim() !== currentOutput.title || !arraysEqual(editorVariants, savedVariants);
  }, [currentOutput, editorState.title, editorVariants]);
  const competitorContextKey = useMemo(
    () => JSON.stringify({
      primary_brand: draft.brand.trim(),
      competitor_set_id: draft.competitor_set_id ?? null,
      competitor_brands: [...draft.competitor_brands].sort((a, b) => a.localeCompare(b)),
      retailer_scope: draft.retailer_scope,
      rating_min: draft.rating_min ?? null,
      rating_max: draft.rating_max ?? null,
      date_from: draft.date_from ?? null,
      date_to: draft.date_to ?? null,
    }),
    [
      draft.brand,
      draft.competitor_brands,
      draft.competitor_set_id,
      draft.date_from,
      draft.date_to,
      draft.rating_max,
      draft.rating_min,
      draft.retailer_scope,
    ],
  );

  const loadBrandProducts = useCallback(
    async (brand: string, options?: { force?: boolean }) => {
      const normalizedBrand = brand.trim();
      if (!normalizedBrand) {
        setProducts([]);
        setProductLoadError(null);
        setIsLoadingProducts(false);
        return;
      }

      const cacheKey = normalizedBrand.toLocaleLowerCase();
      const shouldForce = options?.force ?? false;
      const cachedProducts = shouldForce ? undefined : productCacheRef.current[cacheKey];
      if (cachedProducts) {
        setProducts(cachedProducts);
        setProductLoadError(null);
        setIsLoadingProducts(false);
        return;
      }

      setProducts([]);

      const requestId = productRequestSequenceRef.current + 1;
      productRequestSequenceRef.current = requestId;
      setIsLoadingProducts(true);
      setProductLoadError(null);

      try {
        const nextProducts = await listIndexedBrandProducts(normalizedBrand);
        if (productRequestSequenceRef.current !== requestId) {
          return;
        }

        productCacheRef.current[cacheKey] = nextProducts;
        setProducts(nextProducts);

        const validProducts = new Set(nextProducts.map(product => product.product_name));
        setDraft(currentDraft => {
          if (
            currentDraft.brand.trim() !== normalizedBrand
            || !currentDraft.product_name
            || validProducts.has(currentDraft.product_name)
          ) {
            return currentDraft;
          }
          return { ...currentDraft, product_name: null };
        });
      } catch (error) {
        if (productRequestSequenceRef.current !== requestId) {
          return;
        }

        setProducts([]);
        const message = getApiErrorMessage(error, 'Failed to load indexed products for the selected brand');
        setProductLoadError(message);
        addToast({
          kind: 'error',
          message,
        });
      } finally {
        if (productRequestSequenceRef.current === requestId) {
          setIsLoadingProducts(false);
        }
      }
    },
    [addToast],
  );

  useEffect(() => {
    let isMounted = true;

    const loadDependencies = async () => {
      setIsLoadingDependencies(true);

      try {
        const [nextBrands, nextCompetitorSets, nextBrandVoices] = await Promise.all([
          listIndexedBrands(),
          listCompetitorSets(),
          listBrandVoices(),
        ]);

        if (!isMounted) {
          return;
        }

        setBrands(nextBrands);
        setCompetitorSets(nextCompetitorSets);
        setBrandVoices(nextBrandVoices);

        setDraft(currentDraft => ({
          ...currentDraft,
          brand: currentDraft.brand || (nextBrands.length === 1 ? nextBrands[0].brand : currentDraft.brand),
          competitor_set_id:
            currentDraft.competitor_set_id ?? nextCompetitorSets.find(set => set.is_default)?.id ?? null,
          brand_voice_id:
            currentDraft.brand_voice_id ?? nextBrandVoices.find(config => config.is_default)?.id ?? null,
        }));
      } catch (error) {
        if (isMounted) {
          addToast({
            kind: 'error',
            message: getApiErrorMessage(error, 'Failed to load Bazaarvoice Studio configuration'),
          });
        }
      } finally {
        if (isMounted) {
          setIsLoadingDependencies(false);
        }
      }
    };

    loadDependencies();

    return () => {
      isMounted = false;
    };
  }, [addToast]);

  useEffect(() => {
    void loadBrandProducts(draft.brand);
  }, [draft.brand, loadBrandProducts]);

  useEffect(() => {
    if (!id) {
      setCurrentOutput(null);
      setIsLoadingOutput(false);
      return;
    }

    let isMounted = true;

    const loadOutput = async () => {
      setIsLoadingOutput(true);

      try {
        const output = await getStudioOutput(Number(id));
        if (!isMounted) {
          return;
        }

        const nextDraft = draftFromStudioOutput(output);
        setCurrentOutput(output);
        setDraft(nextDraft);
        setEntryMode(nextDraft.product_name ? 'product-first' : 'template-first');
        setEditorState(buildEditorState(output));
        setSelectedVariantIndex(0);
        setIsSetupOpen(false);
      } catch (error) {
        if (isMounted) {
          addToast({
            kind: 'error',
            message: getApiErrorMessage(error, 'Failed to load Bazaarvoice Studio output'),
          });
        }
      } finally {
        if (isMounted) {
          setIsLoadingOutput(false);
        }
      }
    };

    loadOutput();

    return () => {
      isMounted = false;
    };
  }, [id, addToast]);

  useEffect(() => {
    if (!isCompareOpen) {
      return;
    }
    if (!currentOutput) {
      setCompetitorContext(null);
      setCompetitorContextError('Generate a draft before opening Compare.');
      return;
    }
    if (!draft.brand.trim()) {
      setCompetitorContext(null);
      setCompetitorContextError('Select a brand before opening Compare.');
      return;
    }
    if (!hasCompetitorSelection) {
      setCompetitorContext(null);
      setCompetitorContextError('Select a competitor set or competitor brands before opening Compare.');
      return;
    }

    const cached = competitorContextCache[competitorContextKey];
    if (cached) {
      setCompetitorContext(cached);
      setCompetitorContextError(null);
      return;
    }

    let isMounted = true;
    setIsLoadingCompetitorContext(true);
    setCompetitorContextError(null);

    getStudioCompetitorContext({
      primary_brand: draft.brand,
      competitor_set_id: draft.competitor_set_id,
      competitor_brands: draft.competitor_brands,
      retailer_scope: draft.retailer_scope,
      rating_min: draft.rating_min,
      rating_max: draft.rating_max,
      date_from: draft.date_from,
      date_to: draft.date_to,
    })
      .then((response) => {
        if (!isMounted) {
          return;
        }
        setCompetitorContext(response);
        setCompetitorContextCache(currentCache => ({
          ...currentCache,
          [competitorContextKey]: response,
        }));
      })
      .catch((error) => {
        if (!isMounted) {
          return;
        }
        setCompetitorContext(null);
        setCompetitorContextError(
          getApiErrorMessage(error, 'Failed to build competitor context for the current filter scope'),
        );
      })
      .finally(() => {
        if (isMounted) {
          setIsLoadingCompetitorContext(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [
    competitorContextCache,
    competitorContextKey,
    draft.brand,
    draft.competitor_brands,
    draft.competitor_set_id,
    draft.date_from,
    draft.date_to,
    draft.rating_max,
    draft.rating_min,
    draft.retailer_scope,
    hasCompetitorSelection,
    currentOutput,
    isCompareOpen,
  ]);

  const persistOutput = (output: BVStudioOutput, successMessage: string) => {
    const nextDraft = draftFromStudioOutput(output);
    setCurrentOutput(output);
    setDraft(nextDraft);
    setEntryMode(nextDraft.product_name ? 'product-first' : 'template-first');
    setEditorState(buildEditorState(output));
    setSelectedVariantIndex(0);
    setIsSetupOpen(false);
    addToast({ kind: 'success', message: successMessage });

    if (String(output.id) !== id) {
      navigate(`/bv-reviews/studio/${output.id}`);
    }
  };

  const clearPendingUnsavedAction = () => {
    pendingUnsavedActionRef.current = null;
    setPendingUnsavedActionLabel('continue');
    setIsUnsavedChangesModalOpen(false);
  };

  const queuePendingUnsavedAction = (label: string, action: () => Promise<void> | void) => {
    pendingUnsavedActionRef.current = action;
    setPendingUnsavedActionLabel(label);
    setIsUnsavedChangesModalOpen(true);
  };

  const resetEditorState = (output: BVStudioOutput | null = currentOutput) => {
    setEditorState(buildEditorState(output));
    setSelectedVariantIndex(0);
  };

  const saveDraftEdits = async (successMessage = 'Draft edits saved'): Promise<boolean> => {
    if (!currentOutput || !canEditOutput) {
      return false;
    }
    if (!editorState.title.trim()) {
      addToast({ kind: 'error', message: 'Draft title is required before you save' });
      return false;
    }
    if (!editorVariants[0]?.trim()) {
      addToast({ kind: 'error', message: 'Draft copy cannot be empty' });
      return false;
    }

    setIsSavingEdits(true);
    try {
      const output = await saveStudioOutputEdits(currentOutput.id, {
        title: editorState.title.trim(),
        content: editorVariants[0],
        content_variants: editorVariants.slice(1),
      });
      persistOutput(output, successMessage);
      return true;
    } catch (error) {
      addToast({
        kind: 'error',
        message: getApiErrorMessage(error, 'Failed to save Studio draft edits'),
      });
      return false;
    } finally {
      setIsSavingEdits(false);
    }
  };

  const handleSaveAndContinue = async () => {
    const pendingAction = pendingUnsavedActionRef.current;
    if (!pendingAction) {
      clearPendingUnsavedAction();
      return;
    }

    const didSave = await saveDraftEdits('Draft edits saved');
    if (!didSave) {
      return;
    }

    clearPendingUnsavedAction();
    await pendingAction();
  };

  const handleDiscardAndContinue = async () => {
    const pendingAction = pendingUnsavedActionRef.current;
    resetEditorState();
    clearPendingUnsavedAction();
    if (pendingAction) {
      await pendingAction();
    }
  };

  const runWithUnsavedChangesProtection = (label: string, action: () => Promise<void> | void) => {
    if (hasUnsavedEdits) {
      queuePendingUnsavedAction(label, action);
      return;
    }
    void action();
  };

  const updateSetupDraft = (label: string, updater: (currentDraft: BVStudioDraft) => BVStudioDraft) => {
    const applyUpdate = () => {
      setDraft(currentDraft => normalizeStudioDraft(updater(currentDraft)));
    };

    if (hasWorkspace && hasUnsavedEdits) {
      queuePendingUnsavedAction(label, applyUpdate);
      return;
    }

    applyUpdate();
  };

  const runGenerate = async () => {
    const normalizedDraft = normalizeStudioDraftForRequest(draft);

    if (!normalizedDraft.brand) {
      addToast({ kind: 'error', message: 'Select a brand before generating copy' });
      return;
    }

    if (
      normalizedDraft.rating_min !== null
      && normalizedDraft.rating_max !== null
      && normalizedDraft.rating_min > normalizedDraft.rating_max
    ) {
      addToast({ kind: 'error', message: 'Minimum rating cannot be greater than maximum rating' });
      return;
    }

    setIsGenerating(true);

    try {
      const output = currentOutput
        ? await regenerateStudioOutput(currentOutput.id, normalizedDraft)
        : await generateStudioOutput(normalizedDraft);

      persistOutput(output, currentOutput ? 'Draft regenerated' : 'Draft generated');
    } catch (error) {
      addToast({
        kind: 'error',
        message: getApiErrorMessage(error, 'Failed to generate Bazaarvoice Studio output'),
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerate = () => {
    runWithUnsavedChangesProtection(currentOutput ? 'regenerate this draft' : 'generate a draft', runGenerate);
  };

  const handleApplyEvidenceChip = async (chip: NonNullable<BVStudioOutput['evidence_chips']>[number]) => {
    const runChipAction = async () => {
      const nextDraft = applyEvidenceChipToDraft(draft, chip);
      setDraft(nextDraft);

      if (!currentOutput) {
        return;
      }

      setIsGenerating(true);
      try {
        const output = await regenerateStudioOutput(currentOutput.id, nextDraft);
        persistOutput(output, `${chip.label} applied`);
      } catch (error) {
        addToast({
          kind: 'error',
          message: getApiErrorMessage(error, 'Failed to apply review scope suggestion'),
        });
      } finally {
        setIsGenerating(false);
      }
    };

    runWithUnsavedChangesProtection(`apply "${chip.label}"`, runChipAction);
  };

  const handleStatusTransition = async (status: BVStudioOutput['status'], rejectionReason?: string) => {
    const runTransition = async () => {
      if (!currentOutput) {
        return;
      }

      setIsTransitioning(true);
      try {
        const output = await updateStudioStatus(currentOutput.id, status, rejectionReason);
        setCurrentOutput(output);
        setEditorState(buildEditorState(output));
        addToast({ kind: 'success', message: `Output moved to ${status.replace(/_/g, ' ')}` });
      } catch (error) {
        addToast({
          kind: 'error',
          message: getApiErrorMessage(error, 'Failed to update Studio workflow status'),
        });
      } finally {
        setIsTransitioning(false);
      }
    };

    runWithUnsavedChangesProtection(`move this item to ${status.replace(/_/g, ' ')}`, runTransition);
  };

  const handleCompetitorSetChange = (nextValue: string) => {
    const nextSetId = nextValue ? Number(nextValue) : null;
    const selectedSet = competitorSets.find(set => set.id === nextSetId);

    updateSetupDraft('change the competitor set', currentDraft => ({
      ...currentDraft,
      competitor_set_id: nextSetId,
      competitor_brands: selectedSet ? selectedSet.brand_names : currentDraft.competitor_brands,
    }));
  };

  const setupFieldsDisabled = isGenerating || isSavingEdits;
  const canOpenCompare = Boolean(
    canRead && currentOutput && !isLoadingDependencies && draft.brand.trim() && hasCompetitorSelection,
  );

  const brandAndProductFields = (
    <div className="grid gap-4">
      <BrandSelector
        value={draft.brand}
        onChange={brand =>
          updateSetupDraft('change the brand', currentDraft => ({
            ...currentDraft,
            brand,
            product_name: currentDraft.brand === brand ? currentDraft.product_name : null,
          }))
        }
        brands={brands}
        disabled={isLoadingDependencies || setupFieldsDisabled}
      />

      <div className="grid gap-4 2xl:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="bv-studio-title">Draft title</Label>
          <Input
            id="bv-studio-title"
            value={draft.title || ''}
            onChange={event =>
              updateSetupDraft('change the draft title', currentDraft => ({
                ...currentDraft,
                title: event.target.value,
              }))
            }
            placeholder="Q2 social proof refresh"
            disabled={setupFieldsDisabled}
          />
        </div>

        <ProductSelector
          value={draft.product_name}
          onChange={product_name =>
            updateSetupDraft('change the product', currentDraft => ({
              ...currentDraft,
              product_name,
            }))
          }
          products={products}
          disabled={setupFieldsDisabled}
          isLoading={isLoadingProducts}
          hasBrandSelected={Boolean(draft.brand.trim())}
          loadError={productLoadError}
          onRetry={() => void loadBrandProducts(draft.brand, { force: true })}
        />
      </div>
    </div>
  );

  const templateFields = (
    <TemplateSelector
      value={draft.template_type}
      onChange={template_type =>
        updateSetupDraft('change the template', currentDraft => ({ ...currentDraft, template_type }))
      }
      disabled={setupFieldsDisabled}
      compact
    />
  );

  const entryModeFields = (
    <div className="space-y-3">
      <div>
        <p className="text-sm font-medium text-charcoal dark:text-gray-100">How you want to start</p>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Choose whether you want to begin with a format or a product focus.
        </p>
      </div>

      <RadioGroup
        value={entryMode}
        onValueChange={value => setEntryMode(value as EntryMode)}
        className="grid gap-2 md:grid-cols-2"
        disabled={setupFieldsDisabled}
      >
        <RadioCard value="template-first" className="p-3">
          <p className="text-sm font-medium text-charcoal dark:text-gray-100">Template first</p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Start with the output format, then narrow the brand and product scope.
          </p>
        </RadioCard>
        <RadioCard value="product-first" className="p-3">
          <p className="text-sm font-medium text-charcoal dark:text-gray-100">Product first</p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Start with the brand or product, then choose the kind of copy you need.
          </p>
        </RadioCard>
      </RadioGroup>
    </div>
  );

  const setupPage = (
    <div className="flex flex-1 overflow-y-auto bg-gray-50 p-4 dark:bg-dark-bg xl:p-5">
      <div className="flex min-h-full flex-1 flex-col gap-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="font-subtitle text-h3 text-charcoal dark:text-gray-100">
              {hasWorkspace ? 'Update setup' : 'Set up your draft'}
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Choose a brand, format, and review scope for the next draft.
            </p>
          </div>
          {hasWorkspace ? (
            <Button variant="outline" onClick={() => setIsSetupOpen(false)} disabled={setupFieldsDisabled}>
              Back to workspace
            </Button>
          ) : null}
        </div>

        {!canWrite && !hasWorkspace ? (
          <Alert variant="info" title="Read-only Studio access">
            Open an existing draft from the Library to review copy and source reviews. Generating a new
            draft requires write access.
          </Alert>
        ) : null}

        <div className="space-y-6">
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1.15fr)_minmax(360px,0.85fr)] xl:gap-5">
            <div className="grid min-w-0 content-start gap-5">
              <div className="grid min-w-0 gap-5 rounded-2xl border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-surface">
                {entryMode === 'template-first' ? (
                  <>
                    {entryModeFields}
                    {brandAndProductFields}
                  </>
                ) : (
                  <>
                    {brandAndProductFields}
                    {entryModeFields}
                  </>
                )}
              </div>

              <div className="space-y-4 rounded-2xl border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-surface">
                <div>
                  <p className="text-sm font-medium text-charcoal dark:text-gray-100">Review scope</p>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Narrow or broaden the review set used for this draft.
                  </p>
                </div>

                <div className="grid gap-4 min-[1600px]:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
                  <div className="min-w-0 space-y-4">
                    <div className="space-y-2">
                      <Label>Competitor set</Label>
                      <Select
                        value={draft.competitor_set_id ? String(draft.competitor_set_id) : ''}
                        onValueChange={handleCompetitorSetChange}
                        placeholder="Optional competitor set"
                      >
                        <SelectOption value="">No competitor set</SelectOption>
                        {competitorSets.map(set => (
                          <SelectOption key={set.id} value={String(set.id)}>
                            {set.name}
                          </SelectOption>
                        ))}
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Competitor brands</Label>
                      <MultiSelect
                        values={draft.competitor_brands}
                        onValuesChange={competitor_brands =>
                          updateSetupDraft('change the competitor brands', currentDraft => ({
                            ...currentDraft,
                            competitor_brands,
                          }))
                        }
                        placeholder="Optional competitor brands"
                      >
                        {competitorBrandOptions.map(brand => (
                          <MultiSelectOption key={brand.brand} value={brand.brand}>
                            {brand.brand}
                          </MultiSelectOption>
                        ))}
                      </MultiSelect>
                    </div>
                  </div>

                  <div className="grid min-w-0 gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Retailer scope</Label>
                      <Select
                        value={draft.retailer_scope}
                        onValueChange={retailer_scope =>
                          updateSetupDraft('change the retailer scope', currentDraft => ({
                            ...currentDraft,
                            retailer_scope,
                          }))
                        }
                      >
                        {BV_RETAILER_SCOPE_OPTIONS.map(option => (
                          <SelectOption key={option.value} value={option.value}>
                            {option.label}
                          </SelectOption>
                        ))}
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Brand voice</Label>
                      <Select
                        value={draft.brand_voice_id ? String(draft.brand_voice_id) : ''}
                        onValueChange={value =>
                          updateSetupDraft('change the brand voice', currentDraft => ({
                            ...currentDraft,
                            brand_voice_id: value ? Number(value) : null,
                          }))
                        }
                        placeholder="Optional brand voice"
                      >
                        <SelectOption value="">Default voice</SelectOption>
                        {brandVoices.map(config => (
                          <SelectOption key={config.id} value={String(config.id)}>
                            {config.name}
                          </SelectOption>
                        ))}
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Variants</Label>
                      <Select
                        value={String(draft.num_variants)}
                        onValueChange={value =>
                          updateSetupDraft('change the number of variants', currentDraft => ({
                            ...currentDraft,
                            num_variants: Number(value),
                          }))
                        }
                      >
                        {BV_VARIANT_OPTIONS.map(value => (
                          <SelectOption key={value} value={String(value)}>
                            {value} variants
                          </SelectOption>
                        ))}
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="bv-rating-min">Minimum rating</Label>
                      <Input
                        id="bv-rating-min"
                        type="number"
                        min={1}
                        max={5}
                        step={0.5}
                        value={draft.rating_min ?? ''}
                        onChange={event =>
                          updateSetupDraft('change the minimum rating', currentDraft => ({
                            ...currentDraft,
                            rating_min: event.target.value ? Number(event.target.value) : null,
                          }))
                        }
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="bv-rating-max">Maximum rating</Label>
                      <Input
                        id="bv-rating-max"
                        type="number"
                        min={1}
                        max={5}
                        step={0.5}
                        value={draft.rating_max ?? ''}
                        onChange={event =>
                          updateSetupDraft('change the maximum rating', currentDraft => ({
                            ...currentDraft,
                            rating_max: event.target.value ? Number(event.target.value) : null,
                          }))
                        }
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="bv-date-from">Date from</Label>
                      <Input
                        id="bv-date-from"
                        type="date"
                        value={draft.date_from ?? ''}
                        onChange={event =>
                          updateSetupDraft('change the start date', currentDraft => ({
                            ...currentDraft,
                            date_from: event.target.value || null,
                          }))
                        }
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="bv-date-to">Date to</Label>
                      <Input
                        id="bv-date-to"
                        type="date"
                        value={draft.date_to ?? ''}
                        onChange={event =>
                          updateSetupDraft('change the end date', currentDraft => ({
                            ...currentDraft,
                            date_to: event.target.value || null,
                          }))
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid min-w-0 content-start gap-5 lg:min-h-0 lg:grid-rows-[auto_minmax(0,1fr)]">
              <div className="min-w-0 rounded-2xl border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-surface">
                {templateFields}
              </div>

              <Panel className="flex min-w-0 flex-col lg:min-h-0 lg:overflow-hidden">
                <PanelHeader className="shrink-0">
                  <PanelTitle>Template preview</PanelTitle>
                </PanelHeader>
                <PanelBody className="flex min-w-0 flex-1 flex-col gap-4 lg:min-h-0">
                  <div>
                    <p className="text-sm font-medium text-charcoal dark:text-gray-100">{activeTemplate.label}</p>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{activeTemplate.description}</p>
                  </div>
                  <div className="space-y-2">
                    {TEMPLATE_PREVIEW_CONTENT[draft.template_type].map(line => (
                      <div
                        key={line}
                        className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-3 py-2 text-sm text-charcoal dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-100"
                      >
                        {line}
                      </div>
                    ))}
                  </div>
                  <Alert variant="info" title="What happens next">
                    Generate a draft to open the workspace with copy on the left, source reviews in the
                    middle, and Compare on the right when you need competitor context.
                  </Alert>
                </PanelBody>
              </Panel>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-4 dark:border-dark-border">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {hasWorkspace
                ? 'Update the setup and regenerate when you are ready for a new draft.'
                : 'Generate a first draft to open the working area.'}
            </p>
            <div className="flex flex-wrap gap-3">
              {hasWorkspace ? (
                <Button variant="outline" onClick={() => setIsSetupOpen(false)} disabled={setupFieldsDisabled}>
                  Keep current setup
                </Button>
              ) : null}
              <Button onClick={handleGenerate} disabled={!canWrite || isLoadingDependencies || isGenerating}>
                {isGenerating ? (
                  <>
                    <ArrowPathIcon className="mr-2 h-4 w-4 animate-spin" />
                    {currentOutput ? 'Regenerating' : 'Generating'}
                  </>
                ) : (
                  <>
                    <SparklesIcon className="mr-2 h-4 w-4" />
                    {currentOutput ? 'Regenerate' : 'Generate'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const contextBar = currentOutput ? (
    <div className="border-b border-gray-200 bg-white px-6 py-4 dark:border-dark-border dark:bg-dark-surface">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="brand">{draft.brand || currentOutput.brand_filter}</Badge>
          <Badge variant="default">{activeTemplate.label}</Badge>
          <Badge variant="default">
            {BV_RETAILER_SCOPE_OPTIONS.find(option => option.value === draft.retailer_scope)?.label || 'All retailers'}
          </Badge>
          {draft.product_name ? <Badge variant="default">{draft.product_name}</Badge> : null}
          {draft.date_from || draft.date_to ? (
            <Badge variant="default">
              {draft.date_from || 'Any time'} to {draft.date_to || 'Now'}
            </Badge>
          ) : null}
          {draft.competitor_brands.length > 0 ? (
            <Badge variant="default">vs. {draft.competitor_brands.join(', ')}</Badge>
          ) : null}
        </div>

        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setIsSetupOpen(true)}>
            <AdjustmentsHorizontalIcon className="mr-2 h-4 w-4" />
            Change setup
          </Button>
          <Button
            variant="outline"
            onClick={() => setIsCompareOpen(current => !current)}
            disabled={!canOpenCompare}
          >
            <ArrowsRightLeftIcon className="mr-2 h-4 w-4" />
            {isCompareOpen ? 'Hide Compare' : 'Compare'}
          </Button>
          <Button onClick={handleGenerate} disabled={!canWrite || isGenerating || isLoadingDependencies}>
            {isGenerating ? (
              <>
                <ArrowPathIcon className="mr-2 h-4 w-4 animate-spin" />
                Regenerating
              </>
            ) : (
              <>
                <SparklesIcon className="mr-2 h-4 w-4" />
                Regenerate
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  ) : null;

  const workspacePage = currentOutput ? (
    <>
      {contextBar}
      <div className="flex min-h-0 flex-1 overflow-y-auto bg-gray-50 p-4 dark:bg-dark-bg xl:p-5">
        <div
          className={`grid w-full gap-4 ${isCompareOpen
            ? 'xl:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)] 2xl:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.8fr)_minmax(320px,0.8fr)]'
            : 'xl:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.85fr)]'
          }`}
        >
          <div className="flex min-w-0 flex-col gap-4 2xl:min-h-0 2xl:overflow-hidden">
            <EvidenceChips chips={currentOutput.evidence_chips} onApply={handleApplyEvidenceChip} isBusy={isGenerating} />

            <div className="flex min-w-0 flex-col gap-4 2xl:min-h-0 2xl:flex-1 2xl:overflow-hidden">
              <ContentEditor
                title={editorState.title}
                templateType={currentOutput.template_type}
                status={currentOutput.status}
                variants={editorVariants}
                selectedVariantIndex={selectedVariantIndex}
                onSelectVariant={setSelectedVariantIndex}
                onTitleChange={title => setEditorState(current => ({ ...current, title }))}
                onContentChange={content =>
                  setEditorState(current => ({
                    ...current,
                    variants: current.variants.map((variant, index) =>
                      index === selectedVariantIndex ? content : variant,
                    ),
                  }))
                }
                canEdit={canEditOutput}
                isDirty={hasUnsavedEdits}
                isSaving={isSavingEdits}
                onSave={() => {
                  void saveDraftEdits();
                }}
                className="flex-1"
              />

              <ApprovalControls
                output={currentOutput}
                canTransition={canWrite}
                isPending={isTransitioning}
                onTransition={handleStatusTransition}
              />
            </div>
          </div>

          <div className="flex min-w-0 flex-col gap-4 2xl:min-h-0 2xl:overflow-hidden">
            <EvidenceSummaryPanel summary={currentOutput.evidence_summary} strength={currentOutput.evidence_strength} />
            <CitationPanel
              citations={currentOutput.citation_manifest}
              title="Source reviews"
              scrollable={currentOutput.citation_manifest.length > 3}
              maxHeightClassName="max-h-[340px] sm:max-h-[380px] xl:max-h-[460px] 2xl:max-h-none"
              className="flex min-h-[420px] flex-col overflow-hidden 2xl:min-h-0 2xl:flex-1"
              bodyClassName="flex min-h-[320px] flex-1 flex-col"
              scrollRegionClassName="min-h-[240px]"
            />
          </div>

          {isCompareOpen ? (
            <div className="min-w-0 xl:col-span-2 2xl:col-span-1 2xl:min-h-0">
              <CompetitorPanel
                isLoading={isLoadingCompetitorContext}
                error={competitorContextError}
                context={competitorContext}
              />
            </div>
          ) : null}
        </div>
      </div>
    </>
  ) : (
    <div className="flex min-h-0 flex-1 items-center justify-center bg-gray-50 p-4 dark:bg-dark-bg xl:p-5">
      <Alert variant="info" title="Ready to generate">
        {isLoadingDependencies
          ? 'Loading brands, competitor sets, and brand voices.'
          : 'Use the setup workspace to generate the first draft for this tenant.'}
      </Alert>
    </div>
  );

  return (
    <Layout>
      <Page layout="full-width">
        <PageHeader
          title={<BVPageTitle title="Review Intelligence Studio" />}
          description={
            hasWorkspace
              ? 'Refine AI-generated copy, review source reviews, and move approved drafts through review.'
              : 'Choose a brand, format, and review scope to generate copy based on customer reviews.'
          }
          actions={
            <Button variant="outline" onClick={() => navigate('/bv-reviews/library')}>
              <FolderOpenIcon className="mr-2 h-4 w-4" />
              Open Library
            </Button>
          }
        />

        <PageBody fill padded={false}>
          <div className="flex h-full min-h-0 flex-col overflow-hidden">
            {isLoadingOutput ? (
              <div className="flex min-h-0 flex-1 items-center justify-center bg-gray-50 dark:bg-dark-bg">
                <Spinner size="lg" />
              </div>
            ) : isSetupOpen ? (
              setupPage
            ) : (
              workspacePage
            )}
          </div>
        </PageBody>
      </Page>

      <Modal open={isUnsavedChangesModalOpen} onClose={clearPendingUnsavedAction}>
        <ModalContent size="md">
          <ModalHeader>
            <ModalTitle>Save changes first?</ModalTitle>
          </ModalHeader>
          <ModalBody className="space-y-3">
            <Alert variant="warning" title="Unsaved draft edits">
              You have unsaved changes in the copy panel. Save them before you {pendingUnsavedActionLabel},
              discard them, or cancel.
            </Alert>
          </ModalBody>
          <ModalFooter className="flex flex-wrap justify-between gap-3">
            <Button variant="outline" onClick={clearPendingUnsavedAction} disabled={isSavingEdits}>
              Cancel
            </Button>
            <div className="flex flex-wrap gap-3">
              <Button variant="outline" onClick={() => void handleDiscardAndContinue()} disabled={isSavingEdits}>
                Discard edits
              </Button>
              <Button onClick={() => void handleSaveAndContinue()} disabled={isSavingEdits}>
                {isSavingEdits ? (
                  <>
                    <ArrowPathIcon className="mr-2 h-4 w-4 animate-spin" />
                    Saving
                  </>
                ) : (
                  'Save draft edits'
                )}
              </Button>
            </div>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Layout>
  );
}
